import { useTranslation } from 'react-i18next';
import React, { useState, useMemo } from 'react';
import { generateBatchFinancialCSV, generateBatchFinancialPDF, generateStudentLedgerCSV, generateStudentLedgerPDF, generateBatchMonthlyCSV, generateBatchMonthlyPDF } from '../../utils/financeExportUtils';
import { financeAPI, resolveImage } from '../../utils/api';
import { tField } from '../../utils/bilingualHelper';

export default function BatchDrilldownModal({
  selectedBatch,
  onClose,
  batchStudents,
  loadingStudents,
  expandedStudentId,
  setExpandedStudentId,
  feeHistory,
  loadingHistory,
  communicationHistory,
  loadingCommunication,
  alertContext
}) {
  const { t } = useTranslation();
  const formatMonthYear = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const availableMonths = useMemo(() => {
    if (!selectedBatch?.startDate || !selectedBatch?.endDate) return [];
    const start = new Date(selectedBatch.startDate);
    const end = new Date(selectedBatch.endDate);
    const monthsList = [];
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    
    let curr = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    
    while (curr <= last) {
      monthsList.push(`${months[curr.getMonth()]} ${curr.getFullYear()}`);
      curr.setMonth(curr.getMonth() + 1);
    }
    return monthsList;
  }, [selectedBatch]);

  const [selectedExportMonth, setSelectedExportMonth] = useState('All-Time Summary');
  const [isExporting, setIsExporting] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState(null);

  const handleBatchExport = async (format) => {
    if (selectedExportMonth === 'All-Time Summary') {
    if (format === 'csv') generateBatchFinancialCSV(tField(selectedBatch.name), batchStudents);
    else generateBatchFinancialPDF(tField(selectedBatch.name), batchStudents);
      return;
    }

    setIsExporting(true);
    try {
      const promises = batchStudents.map(s => 
        financeAPI.getStudentFeeHistory(s.studentId, selectedBatch.id || selectedBatch._id)
          .then(res => ({ student: s, history: res.data.history || [] }))
          .catch(() => ({ student: s, history: [] }))
      );
      
      const results = await Promise.all(promises);
      
      const reportData = results.map(r => {
        const monthData = r.history.find(h => `${h.month} ${h.year}` === selectedExportMonth) || {};
        return {
          ...r.student,
          expectedForMonth: monthData.expectedAmount || 0,
          paidForMonth: monthData.paidAmount || 0,
          monthStatus: monthData.status || 'not_applicable'
        };
      }).filter(r => r.monthStatus !== 'not_applicable');

      if (format === 'csv') {
        generateBatchMonthlyCSV(tField(selectedBatch.name), selectedExportMonth, reportData);
      } else {
        generateBatchMonthlyPDF(tField(selectedBatch.name), selectedExportMonth, reportData);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to generate monthly report.");
    } finally {
      setIsExporting(false);
    }
  };

  if (!selectedBatch) return null;

  return (
    <div className="drilldown-modal-overlay" onClick={onClose}>
      
      <div className="drilldown-modal-content" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div style={{
          padding: '24px 32px', borderBottom: '1px solid #f0e6da',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#fcfaf7'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--wine)' }}>{t('financialHub.batchDrilldown')} {tField(selectedBatch.name)}
            </h2>
            <div style={{ fontSize: 13, color: '#7a7068', marginTop: 4 }}>
              Students: <strong>{batchStudents.length}</strong> · Expected: <strong>${selectedBatch.totalExpected?.toLocaleString()}</strong> · Collected: <strong>${selectedBatch.collected?.toLocaleString()}</strong> · Arrears: <strong style={{ color: '#c0392b' }}>${selectedBatch.arrears?.toLocaleString()}</strong>
            </div>
            {(selectedBatch.startDate || selectedBatch.endDate) && (
              <div style={{ fontSize: 12, color: 'var(--wine)', marginTop: 4, fontWeight: 600 }}>
                📅 {t('financialHub.duration')} {formatMonthYear(selectedBatch.startDate)} – {formatMonthYear(selectedBatch.endDate)}
              </div>
            )}
          </div>
          <button 
            onClick={onClose}
            style={{
              background: '#f0e0e8', color: '#7b1d3a', border: 'none',
              width: 36, height: 36, borderRadius: '50%', cursor: 'pointer',
              fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
            ✕
          </button>
        </div>

        {/* Split Content Pane */}
        <div className="drilldown-split-pane">
          
          {/* Left Column: Student Balance List */}
          <div className="drilldown-left-pane">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#3d3730', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('financialHub.enrolledStudents')} ({batchStudents.length})
              </h3>
              {batchStudents.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select 
                    value={selectedExportMonth} 
                    onChange={e => setSelectedExportMonth(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '11px', outline: 'none' }}
                  >
                    <option value="All-Time Summary">All-Time Summary</option>
                    {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <button disabled={isExporting} onClick={() => handleBatchExport('csv')} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#333', opacity: isExporting ? 0.5 : 1 }}>CSV</button>
                  <button disabled={isExporting} onClick={() => handleBatchExport('pdf')} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#7b1d3a', opacity: isExporting ? 0.5 : 1 }}>PDF</button>
                </div>
              )}
            </div>

            {loadingStudents ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: '#7a7068' }}>
                ⏳ Querying financial statuses...
              </div>
            ) : batchStudents.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
                <div style={{ fontWeight: 600, color: '#3d3730' }}>{t('financialHub.noStudents')}</div>
                <p style={{ fontSize: 12, color: '#7a7068', marginTop: 4 }}>{t('financialHub.addStudentsFromBatch')}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '650px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f0e6da' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: 11, textTransform: 'uppercase', color: '#7a7068' }}>{t('financialHub.student')}</th>
                      <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: 11, textTransform: 'uppercase', color: '#7a7068' }}>{t('financialHub.guardian')}</th>
                      <th style={{ textAlign: 'right', padding: '12px 8px', fontSize: 11, textTransform: 'uppercase', color: '#7a7068' }}>{t('financialHub.balance')}</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', fontSize: 11, textTransform: 'uppercase', color: '#7a7068' }}>{t('financialHub.status')}</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px', fontSize: 11, textTransform: 'uppercase', color: '#7a7068' }}>{t('financialHub.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchStudents.map((student) => {
                      const isPaid = student.status === 'paid';
                      const isExpanded = expandedStudentId === student.studentId;

                      return (
                        <React.Fragment key={student.studentId}>
                          <tr 
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedStudentId(null);
                              } else {
                                setExpandedStudentId(student.studentId);
                              }
                            }}
                            style={{ 
                              borderBottom: isExpanded ? 'none' : '1px solid #faf6f0',
                              background: isExpanded ? 'rgba(43, 13, 21, 0.02)' : 'transparent',
                              transition: 'all 0.2s ease',
                              cursor: 'pointer'
                            }}
                            className="table-row-hover">
                            <td style={{ padding: '16px 8px' }}>
                              <div style={{ fontWeight: 600, color: 'var(--wine)' }}>{tField(student.name)} {isExpanded ? '▼' : '🔍'}</div>
                              <div style={{ fontSize: 11, color: '#7a7068' }}>{student.rollNumber || 'STU-ID'}</div>
                            </td>
                            <td style={{ padding: '16px 8px' }}>
                              <div style={{ fontWeight: 500, color: '#3d3730' }}>{tField(student.guardianName)}</div>
                              <div style={{ fontSize: 11, color: 'var(--wine)' }}>{student.guardianPhone}</div>
                            </td>
                            <td style={{ padding: '16px 8px', textAlign: 'right', fontWeight: 700 }}>
                              <div style={{ color: isPaid ? '#2d7a52' : '#c0392b' }}>
                                {t('financialHub.globalBalance')}: ${student.balance}
                              </div>
                              <div style={{ fontSize: 10, color: '#7a7068', fontWeight: 400 }}>Global Paid: ${student.amountPaid}</div>
                            </td>
                            <td style={{ padding: '16px 8px', textAlign: 'center' }}>
                              <span className={`badge ${
                                student.status === 'paid' ? 'badge-green' : 
                                student.status === 'overdue' ? 'badge-red' : 'badge-orange'
                              }`}>
                                {student.status.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '16px 8px', textAlign: 'center' }}>
                              {isPaid ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <span style={{ color: '#2d7a52', fontWeight: 600, fontSize: 13 }}>✓ Up to Date</span>
                                  {student.paidTillMonth && student.paidTillMonth !== 'None' && (
                                    <span style={{ fontSize: 10, color: '#7a7068', marginTop: 2 }}>Paid till {student.paidTillMonth}</span>
                                  )}
                                </div>
                              ) : (
                                <button
                                  className="btn btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    alertContext.openAlert(student);
                                  }}
                                  style={{
                                    background: 'var(--wine)',
                                    color: '#fff',
                                    border: 'none', padding: '6px 12px', borderRadius: 6,
                                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                  ✉️ Alert
                                </button>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ background: 'rgba(43, 13, 21, 0.015)' }}>
                              <td colSpan="5" style={{ padding: '0 16px 16px 16px' }}>
                                <div style={{
                                  background: '#fff',
                                  borderRadius: 16,
                                  border: '1px solid #f0e6da',
                                  padding: '20px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                                  animation: 'slideDown 0.25s ease-out',
                                  cursor: 'default'
                                }} onClick={e => e.stopPropagation()}>
                                  {/* Inner sub-header */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 16,
                                    borderBottom: '1px solid #fcf2e8',
                                    paddingBottom: 10
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ fontSize: 16 }}>📊</span>
                                      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--wine)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Month-by-Month Payment History for {tField(student.name)}
                                      </span>
                                    </div>
                                    <span style={{ fontSize: 11, color: '#7a7068', fontWeight: 500 }}>
                                      Batch Start to End Billing Ledger
                                    </span>
                                  </div>

                                  {/* History Grid */}
                                  {loadingHistory ? (
                                    <div style={{ padding: '20px 0', textAlign: 'center', color: '#7a7068', fontSize: 13 }}>
                                      ⏳ Fetching fee ledger details...
                                    </div>
                                  ) : !feeHistory || feeHistory.length === 0 ? (
                                    <div style={{ padding: '20px 0', textAlign: 'center', color: '#7a7068', fontSize: 13 }}>
                                      No billing history found.
                                    </div>
                                  ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                                      {feeHistory.filter(item => item.status !== 'not_applicable').map((item, idx) => {
                                        const badgeStyle = {
                                          paid: { bg: '#e8f7f0', text: '#2d7a52', label: 'Paid' },
                                          partially_paid: { bg: '#fff4e6', text: '#d97706', label: 'Part Paid' },
                                          overdue: { bg: '#fdf2f2', text: '#c0392b', label: 'Overdue' },
                                          pending: { bg: '#fffaf0', text: '#c0692b', label: 'Pending' }
                                        }[item.status] || { bg: '#eee', text: '#666', label: item.status };

                                        return (
                                          <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div style={{
                                              background: '#fffcf9',
                                              border: '1px solid #f3ebe1',
                                              borderRadius: 12,
                                              padding: '12px 16px',
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              transition: 'all 0.2s ease',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
                                              cursor: item.status !== 'paid' ? 'pointer' : 'default'
                                            }}
                                            onClick={(e) => {
                                              if (item.status !== 'paid') {
                                                e.stopPropagation();
                                                alertContext.openAlert(student, item);
                                              }
                                            }}
                                            onMouseEnter={(e) => {
                                              if (item.status !== 'paid') {
                                                e.currentTarget.style.borderColor = 'var(--wine)';
                                                e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.04)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (item.status !== 'paid') {
                                                e.currentTarget.style.borderColor = '#f3ebe1';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.01)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                              }
                                            }}>
                                              <div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>
                                                  {item.month} {item.year}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#7a7068', marginTop: 2 }}>
                                                  Monthly Fee: ${item.expectedAmount || student.monthlyFee || selectedBatch.standardFee || 1800} HKD
                                                </div>
                                              </div>
                                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                                <span style={{
                                                  background: badgeStyle.bg, color: badgeStyle.text,
                                                  padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 800,
                                                  textTransform: 'uppercase'
                                                }}>
                                                  {badgeStyle.label}
                                                </span>
                                                <span style={{ fontSize: 10, color: '#555', fontWeight: 600 }}>
                                                  Paid: ${item.paidAmount || 0}
                                                </span>
                                                {item.status !== 'paid' && (
                                                  <span style={{ fontSize: 9, color: 'var(--wine)', marginTop: 2, fontWeight: 600 }}>
                                                    Click to Alert
                                                  </span>
                                                )}
                                                {item.receiptUrl && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setPreviewReceipt({ url: resolveImage(item.receiptUrl), title: `${item.month} ${item.year}` });
                                                    }}
                                                    style={{
                                                      background: '#e8f7f0',
                                                      border: '1px solid #2d7a52',
                                                      color: '#2d7a52',
                                                      padding: '2px 8px',
                                                      borderRadius: 4,
                                                      fontSize: 10,
                                                      fontWeight: 700,
                                                      cursor: 'pointer',
                                                      marginTop: 4,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 4
                                                    }}
                                                  >
                                                    <span>📄</span> Receipt
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                            
                                            {/* Complimentary Product Breakdown */}
                                            {item.merchandisePaid > 0 && (
                                              <div style={{
                                                background: '#fff8e6',
                                                border: '1px solid #ffe8aa',
                                                borderRadius: 12,
                                                padding: '10px 16px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                              }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#d97706', fontSize: 12, fontWeight: 600 }}>
                                                  <span>🛍️</span> Includes complimentary product(s)
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, fontSize: 11, fontWeight: 700 }}>
                                                  <span style={{ color: '#92400e' }}>Fee: ${Math.max(0, (item.paidAmount || 0) - item.merchandisePaid)}</span>
                                                  <span style={{ color: '#d97706' }}>Items: ${item.merchandisePaid}</span>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right Column: Dispatch targeted reminder cockpit & Monthly Ledger */}
          <div className="drilldown-right-pane">
            {!expandedStudentId ? (
              <div style={{
                margin: 'auto', textAlign: 'center', padding: '20px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16
              }}>
                <div style={{ fontSize: 44 }}>📋</div>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#3d3730' }}>
                  Student Cockpit & Ledger
                </h4>
                <p style={{ fontSize: 13, color: '#7a7068', lineHeight: 1.6, margin: 0 }}>
                  Select any student from the left table to inspect their month-by-month fee history or dispatch pending balance alerts.
                </p>
                <div style={{
                  background: '#fff', padding: '12px 16px', borderRadius: 12,
                  border: '1px solid #f0e6da', fontSize: 12, color: 'var(--wine)',
                  fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <span>💡</span>
                  Click any student row to load their entire annual fee breakdown!
                </div>
              </div>
            ) : (() => {
              const activeProfile = batchStudents.find(s => s.studentId === expandedStudentId);
              if (!activeProfile) return null;
              
              return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--wine)' }}>
                    Student Profile & Ledger
                  </h4>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#7a7068' }}>
                    Inspect payment boundaries or dispatch internal alert notifications.
                  </p>
                </div>

                {/* Summary Info */}
                <div style={{
                  background: '#fff', border: '1px solid #f0e6da',
                  borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#111', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{tField(activeProfile.name)}</span>
                    <span style={{
                      background: activeProfile.status === 'paid' ? '#e8f7f0' : '#fdf2f2',
                      color: activeProfile.status === 'paid' ? '#2d7a52' : '#c0392b',
                      fontSize: 10, padding: '2px 8px', borderRadius: 6, fontWeight: 800, textTransform: 'uppercase'
                    }}>
                      {activeProfile.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#555' }}>
                    Guardian: <strong>{tField(activeProfile.guardianName)}</strong> · {activeProfile.guardianPhone}
                  </div>
                  <div style={{ fontSize: 12, color: '#555' }}>
                    Batch: <strong>{tField(selectedBatch.name)}</strong> · Monthly Fee: <strong>${activeProfile.monthlyFee || selectedBatch.standardFee || 1800} HKD</strong>
                  </div>
                  <div style={{ fontSize: 12, color: '#555' }}>
                    Complimentary Items: <strong>{activeProfile.purchasedMerchandise && activeProfile.purchasedMerchandise.filter(pm => pm && pm.merchandise && pm.merchandise.name).length > 0 ? activeProfile.purchasedMerchandise.filter(pm => pm && pm.merchandise && pm.merchandise.name).map(pm => tField(pm.merchandise.name)).join(', ') : 'N/A'}</strong>
                  </div>
                  <div style={{ 
                    fontSize: 13, 
                    color: activeProfile.balance > 0 ? '#c0392b' : '#2d7a52', 
                    fontWeight: 800, 
                    borderTop: '1px solid #faf6f0', 
                    paddingTop: 6, 
                    marginTop: 4,
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>Cumulative Arrears:</span>
                    <span>${activeProfile.balance} HKD</span>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3ebe1', paddingBottom: 8, marginBottom: 16 }}>
                  <div style={{
                    padding: '10px 16px',
                    borderBottom: '3px solid var(--wine)',
                    fontWeight: 800,
                    color: 'var(--wine)',
                    fontSize: 13,
                    marginBottom: '-11px'
                  }}>
                    📊 Payment Ledger
                  </div>
                  {feeHistory && feeHistory.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', paddingRight: '4px' }}>
                      <button onClick={() => generateStudentLedgerCSV(tField(activeProfile.name), tField(selectedBatch.name), feeHistory, activeProfile.purchasedMerchandise)} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#333' }}>CSV</button>
                      <button onClick={() => generateStudentLedgerPDF(tField(activeProfile.name), tField(selectedBatch.name), feeHistory, activeProfile.purchasedMerchandise)} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#7b1d3a' }}>PDF</button>
                    </div>
                  )}
                </div>

                {/* Tab Contents */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ 
                      background: '#fff', 
                      border: '1px solid #f0e6da', 
                      borderRadius: 16, 
                      padding: '24px', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      textAlign: 'center',
                      gap: 16,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                      flex: 1,
                      justifyContent: 'center'
                    }}>
                      {/* Modern Initials Avatar */}
                      <div style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, var(--wine) 0%, #7b1d3a 100%)`,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 26,
                        fontWeight: 800,
                        boxShadow: '0 8px 16px rgba(123, 29, 58, 0.2)'
                      }}>
                        {tField(activeProfile.name).charAt(0).toUpperCase()}
                      </div>

                      <div>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 800, color: 'var(--wine)' }}>
                          {tField(activeProfile.name)}
                        </h3>
                        <div style={{ fontSize: 12, color: '#7a7068' }}>
                          Registration ID: <strong>{activeProfile.rollNumber || 'STU-ID'}</strong>
                        </div>
                      </div>

                      {/* Quick Stats Grid */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: 12, 
                        width: '100%', 
                        marginTop: 8 
                      }}>
                        <div style={{ background: '#faf8f5', padding: '12px', borderRadius: 12, border: '1px solid #f0e6da' }}>
                          <div style={{ fontSize: 10, color: '#7a7068', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                            Total Paid
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: '#2d7a52' }}>
                            ${activeProfile.amountPaid || 0} HKD
                          </div>
                        </div>
                        <div style={{ background: activeProfile.balance > 0 ? '#fdf2f2' : '#faf8f5', padding: '12px', borderRadius: 12, border: `1px solid ${activeProfile.balance > 0 ? '#fde2e2' : '#f0e6da'}` }}>
                          <div style={{ fontSize: 10, color: activeProfile.balance > 0 ? '#c0392b' : '#7a7068', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                            Cumulative Arrears
                          </div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: activeProfile.balance > 0 ? '#c0392b' : '#2d7a52' }}>
                            ${activeProfile.balance || 0} HKD
                          </div>
                        </div>
                      </div>

                      {/* Communication History Log */}
                      <div style={{ width: '100%', textAlign: 'left', marginTop: 8, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#3d3730', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0e6da' }}>
                          Communication History
                        </div>
                        {loadingCommunication ? (
                          <div style={{ fontSize: 12, color: '#7a7068', textAlign: 'center', padding: '10px 0' }}>⏳ Loading logs...</div>
                        ) : !communicationHistory || communicationHistory.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#7a7068', textAlign: 'center', padding: '10px 0', background: '#faf8f5', borderRadius: 8 }}>
                            No alerts sent yet.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', paddingRight: 4, flex: 1 }}>
                            {communicationHistory.map((log, idx) => (
                              <div key={log._id || idx} style={{
                                background: '#fffcf9', border: '1px solid #f0e6da', borderRadius: 8, padding: '10px 12px'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--wine)' }}>{log.title}</span>
                                  <span style={{ fontSize: 10, color: '#999' }}>{new Date(log.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div style={{ fontSize: 11, color: '#555', lineHeight: 1.4 }}>{log.message}</div>
                                <div style={{ fontSize: 9, color: '#7a7068', marginTop: 6, textTransform: 'uppercase', fontWeight: 700, background: '#f5f0eb', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}>
                                  Sent to: {log.role}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Quick Actions */}
                      <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 'auto' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedStudentId(null);
                          }}
                          className="btn btn-outline"
                          style={{ flex: 1, padding: '12px' }}>
                          Close Profile
                        </button>
                        {activeProfile.status !== 'paid' && (
                          <button
                            type="button"
                            onClick={() => alertContext.openAlert(activeProfile)}
                            className="btn btn-primary"
                            style={{ flex: 1.5, padding: '12px', background: 'var(--wine)', borderColor: 'var(--wine)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <span>Send Payment Alert</span>
                            <span>➔</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

        </div>

      </div>

      {/* Receipt Lightbox Modal */}
      {previewReceipt && (
        <div
          onClick={() => setPreviewReceipt(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <button
            onClick={() => setPreviewReceipt(null)}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              background: 'rgba(255,255,255,0.15)',
              border: '2px solid rgba(255,255,255,0.4)',
              borderRadius: '50%', width: '44px', height: '44px',
              fontSize: '22px', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1, backdropFilter: 'blur(4px)',
            }}
            title="Close"
          >
            ×
          </button>
          <div style={{ color: '#fff', fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>
            Receipt Preview - {previewReceipt.title}
          </div>
          <img
            src={previewReceipt.url}
            alt="Payment Receipt"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '80vh',
              borderRadius: '10px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              objectFit: 'contain',
              background: '#fff'
            }}
          />
        </div>
      )}
    </div>
  );
}
